import type { LoginRequest, MeResponse, RegisterRequest, ResendVerificationResponseDto, UpdateProfileDto } from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api, apiFormData } from '../shared/api/client';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<MeResponse | null>(null);
  const initializing = ref(true);

  const isAuthenticated = computed(() => user.value !== null);

  async function refresh(): Promise<void> {
    try {
      user.value = await api<MeResponse>('/api/auth/me');
    } catch {
      // 401 or unreachable API — render login instead of a blank shell.
      user.value = null;
    } finally {
      initializing.value = false;
    }
  }

  function applyWorkspaceUnverified(): void {
    if (user.value) {
      user.value = { ...user.value, workspaceSuspended: true };
    }
  }

  async function resendVerification(): Promise<void> {
    await api<ResendVerificationResponseDto>('/api/auth/resend-verification', { method: 'POST' });
  }

  async function login(payload: LoginRequest): Promise<void> {
    const res = await api<MeResponse>('/api/auth/login', { method: 'POST', body: payload });
    user.value = res;
  }

  async function register(payload: RegisterRequest): Promise<void> {
    const res = await api<MeResponse>('/api/auth/register', { method: 'POST', body: payload });
    user.value = res;
  }

  async function logout(): Promise<void> {
    await api<{ ok: true }>('/api/auth/logout', { method: 'POST' });
    user.value = null;
  }

  async function updateProfile(payload: UpdateProfileDto): Promise<void> {
    user.value = await api<MeResponse>('/api/auth/me', { method: 'PATCH', body: payload });
  }

  async function uploadAvatar(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('avatar', file);
    user.value = await apiFormData<MeResponse>('/api/auth/me/avatar', formData);
  }

  async function removeAvatar(): Promise<void> {
    user.value = await api<MeResponse>('/api/auth/me/avatar', { method: 'DELETE' });
  }

  return {
    user,
    initializing,
    isAuthenticated,
    refresh,
    applyWorkspaceUnverified,
    resendVerification,
    login,
    register,
    logout,
    updateProfile,
    uploadAvatar,
    removeAvatar,
  };
});
